/* eslint-disable @typescript-eslint/no-explicit-any */
import { RotateCcw, Undo2 } from "lucide-react";
import { type FC, useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";

import { CustomButton, DialogBody } from "@/components/atoms";
import { DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface ImageEditorProps {
  file: File;
  onSave: (croppedImage: string) => void;
  aspect?: number;
}

const supportedTypes = ["image/jpeg", "image/png", "image/webp"];

const ImageEditor: FC<ImageEditorProps> = ({ file, onSave, aspect = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState<string>("");

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels || !imageUrl) return null;
    const image = await createImage(imageUrl);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const { width, height } = croppedAreaPixels;
    canvas.width = width;
    canvas.height = height;

    ctx.translate(width / 2, height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);
    ctx.translate(-width / 2, -height / 2);

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    const outputType = supportedTypes.includes(file.type)
      ? file.type
      : "image/png";

    return canvas.toDataURL(outputType, 0.95);
  };

  const handleSave = async () => {
    const base64 = await createCroppedImage();
    if (base64) {
      onSave(base64);
    }
  };

  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  useEffect(() => {
    const input = document.querySelector(
      ".dynamic-gradient"
    ) as HTMLInputElement;
    if (input) {
      const percent = ((zoom - 0.5) / (2 - 0.5)) * 100;
      input.style.setProperty("--progress-percent", `${percent}%`);
    }
  }, [zoom]);

  return (
    <DialogBody title="Edit image" classes="gap-6">
      <div className="flex flex-col">
        <div className="relative w-[202px] h-[202px] mx-auto mb-[24px] overflow-hidden rounded-2xl bg-black/55">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            restrictPosition={false}
          />
        </div>

        <div className="flex items-center justify-between gap-2 mb-6">
          <button
            onClick={handleRotate}
            className="flex flex-col gap-1 bg-slate-100 rounded-[5px] px-4 w-[59px] h-[64px] justify-center items-center"
          >
            <RotateCcw className="w-6 h-6 -scale-x-100" />
            <span>Rotate</span>
          </button>

          <div className="flex flex-col bg-slate-100 rounded-[5px] px-[9px] pb-[6px] w-full h-[64px] justify-end items-start gap-[8px]">
            <Input
              type="range"
              min="0.5"
              max="2"
              step="0.01"
              value={zoom}
              onChange={e => setZoom(parseFloat(e.target.value))}
              className="w-full shadow-none p-0 appearance-none h-[9px] bg-slate-200 rounded-full border-none dynamic-gradient"
            />
            <span>Scale image</span>
          </div>

          <button
            onClick={handleReset}
            className="flex flex-col bg-slate-100 gap-1 rounded-[5px] w-[59px] h-[64px] px-4 justify-center items-center"
          >
            <Undo2 className="w-6 h-6" />
            <span>Undo</span>
          </button>
        </div>

        <div className="w-full flex gap-4">
          <DialogClose asChild>
            <CustomButton
              variant="light"
              type="button"
              className="w-[48%]"
              onClick={handleReset}
            >
              Cancel
            </CustomButton>
          </DialogClose>

          <CustomButton type="submit" onClick={handleSave} className="w-[48%]">
            Save
          </CustomButton>
        </div>
      </div>
    </DialogBody>
  );
};

export default ImageEditor;

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", error => reject(error));
    img.src = url;
  });
}
